<template>
  <div class="energy-insights">
    <div v-if="loading" class="insights-loading">
      Načítavam dáta...
    </div>

    <div v-else>
      <!-- Hlavička -->
      <div class="consumption-header">
        <span>Učebňa zožrala: </span>
        <strong class="highlight">{{ totalConsumed.toFixed(2) }} kWh</strong>
        <span> za {{ periodLabel }}</span>
      </div>
      <div class="consumption-subheader">
        To predstavuje: <strong class="highlight">{{ energyCost.toFixed(2) }} €</strong>
      </div>

      <!-- Štatistiky – skryté ak je rozsah viac ako 31 dní -->
      <div v-if="!isLongRange" class="stats-row">
        <div class="stat-box">
          <span class="stat-value highlight">{{ duringLessonsHours }} h</span>
          <span class="stat-label">Počas výučby</span>
        </div>
        <div class="stat-box">
          <span class="stat-value highlight">{{ outsideLessonsHours }} h</span>
          <span class="stat-label">Mimo výučby (standby)</span>
        </div>
        <div class="stat-box">
          <span class="stat-value highlight">{{ standbyPercent }} %</span>
          <span class="stat-label">Podiel standby</span>
        </div>
        <div class="stat-box">
          <span class="stat-value highlight">{{ standbyEnergyCost.toFixed(2) }} €</span>
          <span class="stat-label">Standby náklady</span>
        </div>
      </div>

      <hr v-if="!isLongRange" class="divider" />

      <!-- Ekologické prepočty -->
      <div class="eco-row">
        <span class="eco-text">
          Priemerný solárny panel by potreboval
          <strong class="highlight">{{ solarHours.toFixed(1) }} hodín</strong>
          slnečného svitu na vyprodukovanie spotrebovanej elektriny.
        </span>
      </div>
      <div class="eco-row">
        <span class="eco-text">
          Pri výrobe elektriny bolo vyprodukovaných
          <strong class="highlight">{{ co2kg.toFixed(3) }} kg CO₂</strong>
          <span class="eco-footnote">({{ co2FactorGrams }} g CO₂/kWh)</span>
        </span>
      </div>
      <div class="eco-row">
        <span class="eco-text">
          Na vykompenzovanie by muselo emisie
          <strong class="highlight">počas {{ periodDays }} {{ periodDays === 1 ? 'dňa' : 'dní' }}</strong>
          pohlcovať
          <strong class="highlight">
            {{ treesNeeded }} {{ treesNeeded === 1 ? 'strom' : treesNeeded < 5 ? 'stromy' : 'stromov' }}
          </strong>
          <span class="eco-footnote">(strom absorbuje ~25 kg CO₂/rok)</span>
        </span>
      </div>

      <hr class="divider" />

      <!-- Editovateľná sadzba -->
      <div class="rate-row">
        <label>1 kWh = </label>
        <input
          type="number"
          v-model.number="kwHPrice"
          step="0.01"
          min="0"
          class="rate-input-large"
        />
        <span> €</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { classroomByPath } from "../../../utils/userInfo";

export default {
  name: "EnergyInsights",

  props: {
    graphTimeRange:     { type: String, default: "Deň"             },
    customRangeStart:   { type: Date,   default: null              },
    customRangeEnd:     { type: Date,   default: null              },
    periodLabel:        { type: String, default: "zvolené obdobie" },
    periodDays:         { type: Number, default: 1                 },
    totalConsumed:      { type: Number, default: 0                 },
    subjectConsumption: { type: Number, default: 0                 },
  },

  data() {
    return {
      loading: false,
      isLongRange: false,
      kwHPrice: 0.20,

      co2FactorGrams: 93,
      solarHoursPerKwh: 0.5,
      co2PerTreePerYear: 25,

      duringLessonsHours: 0,
      outsideLessonsHours: 0,
      duringEnergy: 0,
      outsideEnergy: 0,
    };
  },

  computed: {
    energyCost() {
      return this.totalConsumed * this.kwHPrice;
    },
    standbyPercent() {
      const total = this.duringLessonsHours + this.outsideLessonsHours;
      return total === 0 ? 0 : Math.round((this.outsideLessonsHours / total) * 100);
    },
    standbyEnergyCost() {
      const subjectConsumptionCost = this.subjectConsumption * this.kwHPrice;
      return this.energyCost - subjectConsumptionCost;
    },
    solarHours() {
      return this.totalConsumed * this.solarHoursPerKwh;
    },
    co2kg() {
      return (this.totalConsumed * this.co2FactorGrams) / 1000;
    },
    treesNeeded() {
      const co2PerTreePerDay = this.co2PerTreePerYear / 365;
      if (!co2PerTreePerDay || !this.co2kg || !this.periodDays) return 0;
      return Math.ceil(this.co2kg / (co2PerTreePerDay * this.periodDays));
    },
  },

  watch: {
    graphTimeRange()   { this.fetchAndCompute(); },
    customRangeStart() { this.fetchAndCompute(); },
    customRangeEnd()   { this.fetchAndCompute(); },
  },

  mounted() {
    this.fetchAndCompute();
  },

  methods: {
    getTimeRange() {
      const oneDay = 24 * 3600;
      const yearStart = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000);
      let now = Math.floor(Date.now() / 1000);
      let from;
      // Pre Rok nepotrebujeme bloky, stačí vedieť rozsah (výpočty sa nevolajú)
      switch (this.graphTimeRange) {
        case "Deň":
          from = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
          break;
        case "Týždeň":
          from = now - 7 * oneDay;
          break;
        case "Mesiac":
          from = now - 30 * oneDay;
          break;
        case "Rok":
          from = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000);
          break;
        case "Custom":
          from = this.customRangeStart
            ? Math.floor(new Date(this.customRangeStart).setHours(0, 0, 0, 0) / 1000)
            : now - oneDay;
          now = this.customRangeEnd
            ? Math.floor(new Date(this.customRangeEnd).setHours(23, 59, 59, 0) / 1000)
            : now;
          break;
        default:
          from = now - oneDay;
      }
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      if (from < yearStart) {
        from = yearStart;
      }
      return { from, now };
    },

    async fetchAndCompute() {
      const path = this.$route?.path || window.location.pathname;
      const agent = classroomByPath[path];
      if (!agent) return;

      this.loading = true;

      try {
        const { from, now } = this.getTimeRange();
        const durationSeconds = now - from;
        const maxDurationForBlocks = 31 * 24 * 3600; // 31 dní

        // Preskočíme fetch blokov ak je rozsah viac ako 31 dní
        if (durationSeconds > maxDurationForBlocks) {
          this.isLongRange = true;
          this.duringLessonsHours = 0;
          this.outsideLessonsHours = 0;
          this.duringEnergy = 0;
          this.outsideEnergy = 0;
          return;
        }

        this.isLongRange = false;

        const response = await axios.post(`${agent.agentUrl}/rest/history`, {
          startTimestamp: from,
          endTimestamp: now,
          granularity: "block",
        });

        const blocks = response.data?.result || [];

        if (blocks.length === 0) {
          this.duringLessonsHours = 0;
          this.outsideLessonsHours = 0;
          this.duringEnergy = 0;
          this.outsideEnergy = 0;
          return;
        }

        let duringEnergy  = 0;
        let outsideEnergy = 0;
        let duringBlocks  = 0;
        let outsideBlocks = 0;

        blocks.forEach((block) => {
          const energy = isNaN(block.energy) ? 0 : block.energy;
          const hasSubject = block.subject_name && block.subject_name !== "";

          if (hasSubject) {
            duringEnergy += energy;
            duringBlocks++;
          } else {
            outsideEnergy += energy;
            outsideBlocks++;
          }
        });

        this.duringEnergy        = +duringEnergy.toFixed(3);
        this.outsideEnergy       = +outsideEnergy.toFixed(3);
        this.duringLessonsHours  = +((duringBlocks  * 0.25)).toFixed(1);
        this.outsideLessonsHours = +((outsideBlocks * 0.25)).toFixed(1);

      } catch (e) {}
      finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
:root {
  --bg-primary: #0f1624;
  --bg-secondary: #1a2332;
  --bg-tertiary: #242f3d;
  --accent-primary: #6366f1;
  --accent-secondary: #10b981;
  --accent-tertiary: #f59e0b;
  --text-primary: #f3f4f6;
  --text-secondary: #d1d5db;
  --border-subtle: #374151;
}

.energy-insights {
  padding: 20px;
  font-size: 0.95rem;
  color: var(--text-primary);
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
}

.insights-loading {
  text-align: center;
  color: var(--text-secondary);
  padding: 50px 20px;
  font-size: 0.95rem;
}

.consumption-header {
  font-size: 1.15rem;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
}

.consumption-subheader {
  margin-bottom: 20px;
  font-size: 0.95rem;
  color: var(--text-secondary);
}

.highlight {
  color: var(--accent-tertiary);
  font-weight: 700;
  background: rgba(245, 158, 11, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.stats-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-box {
  flex: 1;
  min-width: 110px;
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, rgba(99, 102, 241, 0.1) 100%);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid var(--border-subtle);
  transition: all 0.3s ease;
}

.stat-box:hover {
  border-color: var(--accent-primary);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--accent-primary);
}

.stat-label {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.eco-row { margin-bottom: 12px; }
.eco-text { line-height: 1.6; color: var(--text-primary); }
.eco-footnote { font-size: 0.8rem; color: var(--text-secondary); margin-left: 4px; }

.divider {
  border: none;
  border-top: 1px solid var(--border-subtle);
  margin: 16px 0;
}

.rate-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  padding-top: 12px;
  border-top: 1px solid var(--border-subtle);
}

.rate-row label { color: var(--text-secondary); font-weight: 600; }

.rate-input-large {
  width: 90px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 1rem;
  text-align: center;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.rate-input-large:focus {
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  outline: none;
}
</style>